import net from 'node:net';
/**
 * Parse `SAGE_WEBHOOK_ALLOWED_HOSTS` — comma-separated `host` or
 * `host:port` entries. Whitespace and empty segments are ignored.
 * Hosts are lower-cased so allow-list matching (also lower-cased) is
 * case-insensitive without either side needing to remember to normalize.
 */
export function parseAllowedHosts(raw) {
    if (!raw)
        return [];
    return raw
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map((entry) => {
        const lower = entry.toLowerCase();
        const idx = lower.lastIndexOf(':');
        const portPart = idx > 0 ? lower.slice(idx + 1) : '';
        if (idx > 0 && /^\d+$/.test(portPart)) {
            return { host: lower.slice(0, idx), port: Number(portPart) };
        }
        return { host: lower };
    });
}
/** Build a `WebhookPolicy` from the raw env value, read once at construction time. */
export function buildWebhookPolicy(allowedHostsEnv) {
    return { allowedHosts: parseAllowedHosts(allowedHostsEnv) };
}
/** Whether `hostname:port` matches an allow-list entry — whole-host, case-insensitive. */
export function matchesAllowList(policy, hostname, port) {
    const h = hostname.toLowerCase();
    return policy.allowedHosts.some((e) => e.host === h && (e.port === undefined || e.port === port));
}
// ─── isBlockedAddress ────────────────────────────────────────────────────
function ipv4ToInt(ip) {
    const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(ip);
    if (!m)
        return null;
    const a = Number(m[1]);
    const b = Number(m[2]);
    const c = Number(m[3]);
    const d = Number(m[4]);
    if ([a, b, c, d].some((o) => o < 0 || o > 255))
        return null;
    return ((a << 24) | (b << 16) | (c << 8) | d) >>> 0;
}
function inCidr(ipInt, base, prefixBits) {
    const baseInt = ipv4ToInt(base);
    if (baseInt === null)
        return false;
    const mask = prefixBits === 0 ? 0 : (0xffffffff << (32 - prefixBits)) >>> 0;
    return (ipInt & mask) >>> 0 === (baseInt & mask) >>> 0;
}
/** Every blocked IPv4 range from the spec's "Blocked address ranges" requirement. */
const BLOCKED_IPV4_RANGES = [
    ['127.0.0.0', 8], // loopback
    ['0.0.0.0', 8], // unspecified
    ['169.254.0.0', 16], // link-local (incl. cloud metadata)
    ['10.0.0.0', 8], // private use
    ['172.16.0.0', 12], // private use
    ['192.168.0.0', 16], // private use
    ['100.64.0.0', 10], // carrier-grade NAT
    ['192.0.0.0', 24], // IETF protocol assignments
    ['192.0.2.0', 24], // documentation (TEST-NET-1)
    ['198.18.0.0', 15], // benchmarking
    ['198.51.100.0', 24], // documentation (TEST-NET-2)
    ['203.0.113.0', 24], // documentation (TEST-NET-3)
    ['224.0.0.0', 4], // multicast
    ['240.0.0.0', 4], // reserved (incl. broadcast)
];
function isBlockedIPv4(ip) {
    const n = ipv4ToInt(ip);
    if (n === null)
        return true; // unparseable — fail closed
    return BLOCKED_IPV4_RANGES.some(([base, bits]) => inCidr(n, base, bits));
}
/**
 * Expand any valid textual IPv6 address (including one with an embedded
 * dotted-quad IPv4 tail, e.g. `::ffff:127.0.0.1`) into 8 zero-padded
 * 4-hex-digit groups. Returns null if the input isn't a well-formed
 * IPv6 address.
 */
function expandIPv6(input) {
    let addr = input.toLowerCase();
    // An embedded IPv4 tail (`a:b:c:d:e:f:1.2.3.4`) is rewritten to its
    // two-hex-group equivalent before the normal `::`-expansion logic runs.
    const lastColon = addr.lastIndexOf(':');
    if (lastColon !== -1) {
        const tail = addr.slice(lastColon + 1);
        if (/^\d{1,3}(\.\d{1,3}){3}$/.test(tail)) {
            const n = ipv4ToInt(tail);
            if (n === null)
                return null;
            const hi = (n >>> 16) & 0xffff;
            const lo = n & 0xffff;
            addr = `${addr.slice(0, lastColon + 1)}${hi.toString(16)}:${lo.toString(16)}`;
        }
    }
    let groups;
    if (addr.includes('::')) {
        const halves = addr.split('::');
        if (halves.length !== 2)
            return null;
        const left = halves[0] ?? '';
        const right = halves[1] ?? '';
        const leftParts = left.split(':').filter((p) => p.length > 0);
        const rightParts = right.split(':').filter((p) => p.length > 0);
        const missing = 8 - (leftParts.length + rightParts.length);
        if (missing < 0)
            return null;
        groups = [...leftParts, ...Array(missing).fill('0'), ...rightParts];
    }
    else {
        groups = addr.split(':');
    }
    if (groups.length !== 8)
        return null;
    if (groups.some((g) => !/^[0-9a-f]{1,4}$/.test(g)))
        return null;
    return groups.map((g) => g.padStart(4, '0'));
}
function isBlockedIPv6(raw) {
    const groups = expandIPv6(raw);
    if (!groups)
        return true; // unparseable — fail closed
    // IPv4-mapped (::ffff:a.b.c.d): normalize to the IPv4 form and defer
    // to the IPv4 table so every spelling of a blocked address is blocked.
    if (groups[0] === '0000' &&
        groups[1] === '0000' &&
        groups[2] === '0000' &&
        groups[3] === '0000' &&
        groups[4] === '0000' &&
        groups[5] === 'ffff') {
        const hi = parseInt(groups[6] ?? '0', 16);
        const lo = parseInt(groups[7] ?? '0', 16);
        const ipv4 = `${(hi >> 8) & 0xff}.${hi & 0xff}.${(lo >> 8) & 0xff}.${lo & 0xff}`;
        return isBlockedIPv4(ipv4);
    }
    if (groups.every((g) => g === '0000'))
        return true; // ::  (unspecified)
    const isLoopback = groups.slice(0, 7).every((g) => g === '0000') && groups[7] === '0001';
    if (isLoopback)
        return true; // ::1
    const g0 = parseInt(groups[0] ?? '0', 16);
    if ((g0 & 0xffc0) === 0xfe80)
        return true; // fe80::/10 link-local
    if ((g0 & 0xfe00) === 0xfc00)
        return true; // fc00::/7 unique local
    if ((g0 & 0xff00) === 0xff00)
        return true; // ff00::/8 multicast
    return false;
}
/**
 * True if `ip` (a literal, already-resolved textual IPv4 or IPv6
 * address) falls in a blocked range. Pure — no I/O. IPv4-mapped and
 * IPv4-compatible IPv6 spellings are normalized to IPv4 first so every
 * spelling of a blocked address is caught.
 */
export function isBlockedAddress(ip) {
    return ip.includes(':') ? isBlockedIPv6(ip) : isBlockedIPv4(ip);
}
// ─── assertAllowedWebhookUrl ─────────────────────────────────────────────
/**
 * Thrown by the guard. `code` is a stable machine-readable reason; the
 * message carries the *caller-supplied* hostname only — never a
 * resolved address, which would be a DNS oracle for internal names
 * (see design.md D5).
 */
export class WebhookDestinationError extends Error {
    code;
    constructor(code, host) {
        super(`${code}: ${host}`);
        this.name = 'WebhookDestinationError';
        this.code = code;
    }
}
function stripBrackets(hostname) {
    return hostname.startsWith('[') && hostname.endsWith(']') ? hostname.slice(1, -1) : hostname;
}
/**
 * Syntactic policy on a webhook destination URL — scheme, embedded
 * credentials, and (for IP-literal hosts) the address itself, checked
 * directly with no DNS call. Everything DNS-dependent (resolution-time
 * validation) lives in `apps/api/src/delivery/url-guard.ts`'s
 * `createGuardedLookup` instead. Pure and synchronous — safe to use
 * inside a Zod `superRefine` at write time.
 */
export function assertAllowedWebhookUrl(raw, policy) {
    let url;
    try {
        url = new URL(raw);
    }
    catch {
        throw new WebhookDestinationError('webhook.destination.invalid', raw);
    }
    if (url.username || url.password) {
        throw new WebhookDestinationError('webhook.destination.credentials', url.hostname);
    }
    const hostname = url.hostname.toLowerCase();
    const port = url.port ? Number(url.port) : url.protocol === 'https:' ? 443 : 80;
    const allowListed = matchesAllowList(policy, hostname, port);
    if (url.protocol !== 'https:') {
        if (!(url.protocol === 'http:' && allowListed)) {
            throw new WebhookDestinationError('webhook.destination.scheme', hostname);
        }
    }
    const literal = stripBrackets(hostname);
    if (net.isIP(literal) !== 0 && !allowListed && isBlockedAddress(literal)) {
        throw new WebhookDestinationError('webhook.destination.blocked', hostname);
    }
    return url;
}
//# sourceMappingURL=webhook-url-guard.js.map